import {
  cancelChatAttachmentUpload,
  useMutateDeleteChatAttachment,
  useMutateUploadChatAttachment,
} from '@/hooks/mutation/useMutateUploadChatAttachment';
import {
  CHAT_ATTACHMENT_MAX_SIZE_BYTES,
  CHAT_ATTACHMENT_MAX_SIZE_MB,
  getUniqueFileName,
} from '@heidi/utils';
import { type AxiosError } from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Shape the hook needs from an upload mutation. Mirrors the
 * ``useMutation`` ``mutateAsync`` signature for chat attachments —
 * any surface that wants to reuse this hook (Projects in particular)
 * just needs an upload that returns ``{ id, url }`` for the same
 * { uid, file, onProgressUpdate } input.
 */
export interface UseChatFileUploadAdapter {
  uploadAttachment: (variables: {
    uid: string;
    file: File;
    onProgressUpdate?: (progress: number) => void;
  }) => Promise<{ id: string; url: string }>;
  deleteAttachment: (attachmentId: string) => Promise<void>;
  /** Cancel an in-flight upload by ``uid`` (returns true if cancelled). */
  cancelUpload: (uid: string) => boolean;
}

// Base fields shared by all file states
type ChatUploadFileBase = {
  uid: string;
  name: string;
  size: number;
  type: string;
  file: File;
};

// Discriminated union variants
type PendingFile = ChatUploadFileBase & {
  uploadStatus: 'pending';
};

type UploadingFile = ChatUploadFileBase & {
  uploadStatus: 'uploading';
  uploadProgress: number; // 0-100
};

type CompletedFile = ChatUploadFileBase & {
  uploadStatus: 'completed';
  attachmentId: string;
  previewUrl: string;
};

export type ChatUploadErrorCode =
  | 'file_too_large'
  | 'unsupported_type'
  | 'rate_limited'
  | 'upload_failed';

const UPLOAD_ERROR_MESSAGES = {
  file_too_large: 'File is too large',
  unsupported_type: 'Unsupported file type',
  rate_limited: 'Upload failed',
  upload_failed: 'Upload failed',
} satisfies Record<ChatUploadErrorCode, string>;

const classifyUploadError = (
  status: number | undefined,
  backendDetail: string
): ChatUploadErrorCode => {
  if (status === 429) return 'rate_limited';
  if (status !== 400) return 'upload_failed';
  if (/exceeds maximum allowed size/i.test(backendDetail)) return 'file_too_large';
  if (
    /unsupported file type|not.*supported|invalid (?:file )?type/i.test(backendDetail)
  ) {
    return 'unsupported_type';
  }
  return 'upload_failed';
};

type ErrorFile = ChatUploadFileBase & {
  uploadStatus: 'error';
  error: string;
  errorCode?: ChatUploadErrorCode;
};

// Union type (exported)
export type ChatUploadFile = PendingFile | UploadingFile | CompletedFile | ErrorFile;

// Status type derived from union (for external use)
export type ChatUploadFileStatus = ChatUploadFile['uploadStatus'];

// Type guards
export const isPendingFile = (file: ChatUploadFile): file is PendingFile =>
  file.uploadStatus === 'pending';

export const isUploadingFile = (file: ChatUploadFile): file is UploadingFile =>
  file.uploadStatus === 'uploading';

export const isCompletedFile = (file: ChatUploadFile): file is CompletedFile =>
  file.uploadStatus === 'completed';

export const isErrorFile = (file: ChatUploadFile): file is ErrorFile =>
  file.uploadStatus === 'error';

/**
 * Check if a file is currently uploading or pending upload.
 * Useful for determining loading state in UI components.
 */
export const isFileUploading = (file: ChatUploadFile): boolean =>
  isPendingFile(file) || isUploadingFile(file);

// Safe property accessors for cross-variant access
export const getUploadProgress = (file: ChatUploadFile): number | undefined =>
  isUploadingFile(file) ? file.uploadProgress : undefined;

export const getPreviewUrl = (file: ChatUploadFile): string | undefined =>
  isCompletedFile(file) ? file.previewUrl : undefined;

export const getErrorMessage = (file: ChatUploadFile): string | undefined =>
  isErrorFile(file) ? file.error : undefined;

export const getErrorCode = (file: ChatUploadFile): ErrorFile['errorCode'] | undefined =>
  isErrorFile(file) ? file.errorCode : undefined;

interface UseChatFileUploadOptions {
  /** Initial files to populate (e.g., from draft persistence) */
  initialFiles?: File[];
  /** Callback when files change (for draft persistence) */
  onFilesChange?: (files: File[]) => void;
  /** Whether to enable upload on attach (default: true) */
  enableUpload?: boolean;
  /** Callback when a file upload fails. Use to show toast notifications. */
  onUploadError?: (
    fileName: string,
    errorMessage: string,
    errorCode: ChatUploadErrorCode
  ) => void;
  /** Formats user-facing inline copy at the React surface via ``useIntl``. */
  formatErrorMessage?: (errorCode: ChatUploadErrorCode) => string;
  /**
   * Override the upload + delete + cancel adapter. Defaults to the chat
   * attachment endpoints (``POST /chat/attachments`` etc.) so Evidence
   * Chat keeps working with no caller changes. The Projects composer
   * passes its own adapter that targets ``POST /projects/{id}/attachments``
   * — same lifecycle, different surface.
   */
  adapter?: UseChatFileUploadAdapter;
}

// Animation constants for fake progress
const FAKE_PROGRESS_DURATION = 3000; // 3 seconds to reach 90%
const FAKE_PROGRESS_TARGET = 90;
const FAKE_PROGRESS_INTERVAL = 50; // Update every 50ms

/**
 * Hook for handling file uploads in the chat input.
 * Manages selected files for attaching to chat messages.
 * When enableUpload is true (default), files are uploaded immediately on attach.
 */
export const useChatFileUpload = (options: UseChatFileUploadOptions = {}) => {
  const {
    initialFiles,
    onFilesChange,
    enableUpload = true,
    onUploadError,
    formatErrorMessage,
    adapter,
  } = options;

  // Always call the chat-attachment hooks at the top level so the
  // Rules of Hooks aren't violated when callers omit ``adapter``. The
  // returned ``mutateAsync`` is unused on the override path — it's a
  // free Query Client subscription either way.
  const { mutateAsync: defaultUploadAttachment } = useMutateUploadChatAttachment();
  const { mutateAsync: defaultDeleteAttachment } = useMutateDeleteChatAttachment();
  const uploadAttachment = adapter?.uploadAttachment ?? defaultUploadAttachment;
  const deleteAttachment = adapter?.deleteAttachment ?? defaultDeleteAttachment;
  const cancelUpload = adapter?.cancelUpload ?? cancelChatAttachmentUpload;

  // `setFiles` updaters can run twice (Strict Mode), and the server delete is
  // issued from inside one. This guard stops a second DELETE from 404ing.
  const deletedAttachmentIdsRef = useRef<Set<string>>(new Set());

  // Fire-and-forget delete of a completed upload, at most once per id. A failure
  // has nothing to roll back; the default mutation logs it in its own `onError`.
  const deleteUploadedAttachment = useCallback(
    (attachmentId: string) => {
      if (deletedAttachmentIdsRef.current.has(attachmentId)) return;
      deletedAttachmentIdsRef.current.add(attachmentId);
      deleteAttachment(attachmentId).catch(() => undefined);
    },
    [deleteAttachment]
  );

  // Track fake progress animations
  const fakeProgressIntervalsRef = useRef<Map<string, ReturnType<typeof setInterval>>>(
    new Map()
  );
  // Strict Mode runs state updaters twice; dedupe upload scheduling per uid.
  const uploadScheduledRef = useRef<Set<string>>(new Set());

  // Convert initial File[] to ChatUploadFile[] for initial state (only on first render)
  const getInitialState = (): ChatUploadFile[] => {
    if (!initialFiles || !Array.isArray(initialFiles) || initialFiles.length === 0)
      return [];
    return initialFiles.map(
      (file, index): ChatUploadFile => ({
        uid: `initial-${index}-${file.name}`,
        name: file.name,
        size: file.size,
        type: file.type,
        file,
        uploadStatus: 'pending',
      })
    );
  };

  const [files, setFilesInternal] = useState<ChatUploadFile[]>(getInitialState);

  // Store callbacks in refs to avoid triggering effects when they change
  const onFilesChangeRef = useRef(onFilesChange);
  onFilesChangeRef.current = onFilesChange;

  const onUploadErrorRef = useRef(onUploadError);
  onUploadErrorRef.current = onUploadError;

  // Custom setter that also notifies parent of changes
  const setFiles = useCallback(
    (
      newFilesOrUpdater: ChatUploadFile[] | ((prev: ChatUploadFile[]) => ChatUploadFile[])
    ) => {
      setFilesInternal((prev) => {
        const newFiles =
          typeof newFilesOrUpdater === 'function'
            ? newFilesOrUpdater(prev)
            : newFilesOrUpdater;

        // Only notify if files actually changed (compare by file reference)
        const prevFileRefs = prev.map((f) => f.file);
        const newFileRefs = newFiles.map((f) => f.file);
        const hasChanged =
          prevFileRefs.length !== newFileRefs.length ||
          prevFileRefs.some((f, i) => f !== newFileRefs[i]);

        if (hasChanged && onFilesChangeRef.current) {
          // Use setTimeout to avoid state update during render
          setTimeout(() => {
            onFilesChangeRef.current?.(newFiles.map((f) => f.file));
          }, 0);
        }

        return newFiles;
      });
    },
    []
  );

  // Type-safe state transition helpers
  // Mark file as uploading (pending → uploading)
  const markUploading = useCallback(
    (uid: string) => {
      setFiles((prev) =>
        prev.map(
          (f): ChatUploadFile =>
            f.uid === uid
              ? {
                  uid: f.uid,
                  name: f.name,
                  size: f.size,
                  type: f.type,
                  file: f.file,
                  uploadStatus: 'uploading',
                  uploadProgress: 0,
                }
              : f
        )
      );
    },
    [setFiles]
  );

  // Update progress during upload (uploading only)
  const setUploadProgress = useCallback(
    (uid: string, progress: number) => {
      setFiles((prev) =>
        prev.map((f): ChatUploadFile => {
          if (f.uid === uid && isUploadingFile(f)) {
            return { ...f, uploadProgress: Math.max(f.uploadProgress, progress) };
          }
          return f;
        })
      );
    },
    [setFiles]
  );

  // Mark upload as completed (uploading → completed)
  const markCompleted = useCallback(
    (uid: string, result: { attachmentId: string; previewUrl: string }) => {
      setFiles((prev) =>
        prev.map(
          (f): ChatUploadFile =>
            f.uid === uid
              ? {
                  uid: f.uid,
                  name: f.name,
                  size: f.size,
                  type: f.type,
                  file: f.file,
                  uploadStatus: 'completed',
                  attachmentId: result.attachmentId,
                  previewUrl: result.previewUrl,
                }
              : f
        )
      );
    },
    [setFiles]
  );

  // Mark upload as failed (any state → error)
  const markError = useCallback(
    (uid: string, errorMessage: string, errorCode?: ErrorFile['errorCode']) => {
      setFiles((prev) =>
        prev.map(
          (f): ChatUploadFile =>
            f.uid === uid
              ? {
                  uid: f.uid,
                  name: f.name,
                  size: f.size,
                  type: f.type,
                  file: f.file,
                  uploadStatus: 'error',
                  error: errorMessage,
                  errorCode,
                }
              : f
        )
      );
    },
    [setFiles]
  );

  // Start fake progress animation for a file
  const startFakeProgress = useCallback(
    (uid: string) => {
      const startTime = Date.now();

      const interval = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(
          FAKE_PROGRESS_TARGET,
          (elapsed / FAKE_PROGRESS_DURATION) * FAKE_PROGRESS_TARGET
        );

        setFiles((prev) =>
          prev.map((f): ChatUploadFile => {
            if (f.uid === uid && isUploadingFile(f)) {
              // Use whichever is higher - fake progress or real progress
              return { ...f, uploadProgress: Math.max(f.uploadProgress, progress) };
            }
            return f;
          })
        );

        // Stop when we reach target
        if (elapsed >= FAKE_PROGRESS_DURATION) {
          clearInterval(interval);
          fakeProgressIntervalsRef.current.delete(uid);
        }
      }, FAKE_PROGRESS_INTERVAL);

      fakeProgressIntervalsRef.current.set(uid, interval);
    },
    [setFiles]
  );

  // Stop fake progress animation for a file
  const stopFakeProgress = useCallback((uid: string) => {
    const interval = fakeProgressIntervalsRef.current.get(uid);
    if (interval) {
      clearInterval(interval);
      fakeProgressIntervalsRef.current.delete(uid);
    }
  }, []);

  // Upload a single file
  const uploadFile = useCallback(
    async (chatFile: ChatUploadFile) => {
      const { uid, name, size, file } = chatFile;

      // Client-side file size validation (avoids unnecessary network round-trip)
      if (size > CHAT_ATTACHMENT_MAX_SIZE_BYTES) {
        const errorMessage = `File exceeds the ${CHAT_ATTACHMENT_MAX_SIZE_MB}MB limit`;
        markError(uid, errorMessage, 'file_too_large');
        onUploadErrorRef.current?.(name, errorMessage, 'file_too_large');
        return;
      }

      // pending → uploading
      markUploading(uid);
      startFakeProgress(uid);

      try {
        // Create a new File with the deduplicated name (chatFile.name may differ from file.name)
        const fileToUpload =
          file.name !== name ? new File([file], name, { type: file.type }) : file;

        const response = await uploadAttachment({
          uid,
          file: fileToUpload,
          onProgressUpdate: (progress) => {
            setUploadProgress(uid, progress);
          },
        });

        // uploading → completed
        stopFakeProgress(uid);
        markCompleted(uid, {
          attachmentId: response.id,
          previewUrl: response.url,
        });
      } catch (error) {
        stopFakeProgress(uid);

        // Check if it was cancelled
        if (
          error instanceof Error &&
          (error.name === 'AbortError' || error.name === 'CanceledError')
        ) {
          // File was removed during upload, don't update state
          return;
        }

        // Classify the failure from the backend response without surfacing
        // the raw `detail` string to the UI — backend prose is unlocalised,
        // unstable, and often duplicates the filename / lists every accepted
        // extension verbatim (see "chestpain.aac" toast regression).
        const axiosError = error as AxiosError<{ detail?: string }>;
        const backendDetail = axiosError?.response?.data?.detail ?? '';
        const status = axiosError?.response?.status;

        // TODO(evidence-pod): replace these regex sniff-tests with a
        // structured backend error code once the upload endpoint returns
        // one (e.g. `{ code: 'file_too_large' }`). The current detection
        // is intentionally lossy — any wording drift on the server falls
        // through to `upload_failed` rather than mis-classifying — but
        // it does mean copy changes on the backend silently degrade UX
        // until the regexes here are updated.
        const errorCode = classifyUploadError(status, backendDetail);

        // Short, human, code-keyed error stored on the file card. The
        // surface (e.g. ChatInput) maps `errorCode` to a localised toast
        // and tooltip — this string is a non-localised fallback for cards
        // rendered without a surface override.
        const errorMessage =
          formatErrorMessage?.(errorCode) ?? UPLOAD_ERROR_MESSAGES[errorCode];

        markError(uid, errorMessage, errorCode);

        // Notify parent of the upload failure (for toast notifications, etc.)
        onUploadErrorRef.current?.(name, errorMessage, errorCode);
      }
    },
    [
      uploadAttachment,
      markUploading,
      markCompleted,
      markError,
      setUploadProgress,
      startFakeProgress,
      stopFakeProgress,
      formatErrorMessage,
    ]
  );

  // Schedule uploads for files (deferred to avoid state batching issues)
  const scheduleUploads = useCallback(
    (filesToUpload: ChatUploadFile[]) => {
      if (!enableUpload || filesToUpload.length === 0) return;

      filesToUpload.forEach((chatFile) => {
        if (uploadScheduledRef.current.has(chatFile.uid)) return;
        uploadScheduledRef.current.add(chatFile.uid);
        void uploadFile(chatFile);
      });
    },
    [enableUpload, uploadFile]
  );

  /**
   * Add files directly from a drag-and-drop event or programmatic file selection.
   * Automatically deduplicates file names (e.g., file.png, file_1.png, file_2.png).
   */
  const addFiles = useCallback(
    (droppedFiles: File[]) => {
      if (droppedFiles.length === 0) return;

      setFiles((prev) => {
        // Collect existing names from current files
        const existingNames = new Set(prev.map((f) => f.name));

        // Build new files with deduplicated names as PendingFile
        const newChatFiles = droppedFiles.map((file, index): ChatUploadFile => {
          const uniqueName = getUniqueFileName(file.name, existingNames);
          // Add to set so subsequent files in this batch don't conflict
          existingNames.add(uniqueName);

          return {
            uid: `drop-${Date.now()}-${index}`,
            name: uniqueName,
            size: file.size,
            type: file.type,
            file,
            uploadStatus: 'pending',
          };
        });

        // Schedule from inside the updater so uploads always match the batch
        // we append, even if React defers the functional update.
        queueMicrotask(() => scheduleUploads(newChatFiles));

        return [...prev, ...newChatFiles];
      });
    },
    [setFiles, scheduleUploads]
  );

  const removeFile = useCallback(
    (uid: string) => {
      setFiles((prev) => {
        const fileToRemove = prev.find((f) => f.uid === uid);
        if (fileToRemove) {
          uploadScheduledRef.current.delete(uid);
          // Stop any fake progress animation
          stopFakeProgress(uid);

          // If upload is in progress, cancel it
          if (isUploadingFile(fileToRemove)) {
            cancelUpload(uid);
          }

          // If upload was completed, delete from server
          if (isCompletedFile(fileToRemove)) {
            deleteUploadedAttachment(fileToRemove.attachmentId);
          }
        }

        return prev.filter((f) => f.uid !== uid);
      });
    },
    [setFiles, stopFakeProgress, deleteUploadedAttachment, cancelUpload]
  );

  /**
   * Clear all files from local state.
   * @param deleteFromServer - Whether to also delete completed uploads from server.
   *   Use `false` when clearing after send (attachments are now part of the message).
   *   Use `true` (default) when user manually clears without sending.
   */
  const clearFiles = useCallback(
    (deleteFromServer = true) => {
      // Use functional update to avoid race condition with stale closure
      setFiles((currentFiles) => {
        currentFiles.forEach((file) => {
          stopFakeProgress(file.uid);

          if (isUploadingFile(file)) {
            cancelUpload(file.uid);
          }

          // Only delete completed uploads from server if explicitly requested
          if (deleteFromServer && isCompletedFile(file)) {
            deleteUploadedAttachment(file.attachmentId);
          }
        });

        return [];
      });
    },
    [setFiles, stopFakeProgress, deleteUploadedAttachment, cancelUpload]
  );

  /**
   * Retry uploading a failed file
   */
  const retryUpload = useCallback(
    (uid: string) => {
      const file = files.find((f) => f.uid === uid);
      if (file && isErrorFile(file)) {
        uploadScheduledRef.current.delete(uid);
        void uploadFile(file);
      }
    },
    [files, uploadFile]
  );

  // Trigger uploads for pending files on mount (for initial files)
  useEffect(() => {
    if (!enableUpload) return;

    const pendingFiles = files.filter(isPendingFile);
    pendingFiles.forEach((file) => {
      void uploadFile(file);
    });
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup intervals on unmount
  useEffect(() => {
    const intervalsMap = fakeProgressIntervalsRef.current;
    return () => {
      intervalsMap.forEach((interval) => clearInterval(interval));
      intervalsMap.clear();
    };
  }, []);

  // Computed values
  const isUploading = useMemo(() => files.some(isFileUploading), [files]);

  const hasErrors = useMemo(() => files.some(isErrorFile), [files]);

  const getAttachmentIds = useCallback(
    (): string[] => files.filter(isCompletedFile).map((f) => f.attachmentId),
    [files]
  );

  /**
   * Get file metadata for timeline entry (name, type, file_key, url, attachment_id)
   */
  const getFileMetadata = useCallback(
    () =>
      files.filter(isCompletedFile).map((f) => ({
        name: f.name,
        type: f.type,
        url: f.previewUrl,
        attachment_id: f.attachmentId,
      })),
    [files]
  );

  return {
    files,
    addFiles,
    removeFile,
    clearFiles,
    retryUpload,
    isUploading,
    hasErrors,
    getAttachmentIds,
    getFileMetadata,
  };
};
